import React from 'react'
import { css } from '@emotion/core'
import { HeaderBlock, Body1, Button, Divider, Separator, useAtSize } from '@fs/zion-ui'
import { useTranslation } from 'react-i18next'
import { boolAttr } from '@fs/zion-frontend-friends'
import { i18n } from '@fs/zion-locale'
import { useLocation } from '@fs/zion-router'
import portraitImage from './assets/landing-portrait.jpg'
import recordImage from './assets/landing-record.png'
import { getExternalBaseUrl } from '../../services/url-service'
import useDefaultScripts from '../../services/default-scripts-service'
import sendAnalytics from '../../services/analytics-helper'

const homePageBodyCss = css`
  margin-left: auto;
  margin-right: auto;
  max-width: 1000px;
  padding: 36px;
`

const surnameContainerCss = css`
  text-align: center;
  width: 100%;
`

const sectionContainerCss = css`
  display: flex;
  justify-content: space-between;
  align-items: center;
  & > *:nth-of-type(1) {
    margin-right: 44px;
  }
  &[is-mobile] {
    flex-direction: column;
    align-items: center;
    & > *:nth-of-type(1) {
      margin-right: 0;
    }
  }
`

const imageCss = css`
  height: 256px;
  width: 256px;
`

export default function HomePageBody(): JSX.Element {
  const atSize = useAtSize()
  const isMobile = atSize({ default: true, lg: false })
  const [t] = useTranslation()
  const location = useLocation()
  const scripts = useDefaultScripts() || {}
  const doExternalLink = location.pathname.endsWith(`/${i18n.language}`) || window?.location?.hostname === 'localhost'

  return (
    <div css={homePageBodyCss}>
      <div css={surnameContainerCss}>
        <HeaderBlock
          size="sm"
          heading={t('ancestors.surname-directory.header', 'Browse our surname directory today.')}
        />
        <Body1>
          {t(
            'ancestors.surname-directory.description',
            "Bring to life your family's history by exploring the lives of those that came before you. Find your ancestors, and see all their photos, stories, and historical documents."
          )}
        </Body1>
        <Button
          data-testid="browse-surnames"
          emphasis="billboard"
          to={`${doExternalLink ? `/${i18n.language}` : ''}/surnames/${scripts[i18n.language] || 'latn'}`}
          external={doExternalLink}
          onClick={() => {
            sendAnalytics({ link_page: 'Home', link_name: 'Browse Surname Directory' })
          }}
        >
          {t('ancestors.surname-directory.action', 'Browse Surname Directory')}
        </Button>
      </div>
      <Separator size="md" />
      <Divider mode="middle" />
      <Separator size="md" />
      <div css={sectionContainerCss} is-mobile={boolAttr(isMobile)}>
        <img src={portraitImage} css={imageCss} alt={t('ancestors.portrait.alt', 'Portrait')} />
        {isMobile && <Separator size="md" />}
        <div>
          <HeaderBlock
            size="sm"
            heading={t('ancestors.memories-section.header', 'See what your ancestors looked like.')}
          />
          <Body1>
            {t(
              'ancestors.memories-section.description',
              'At FamilySearch, we preserve memories, so they can be shared for generations to come. Come see what we have, and add your unique photos, stories, and documents to your ancestors.'
            )}
          </Body1>
          <Button
            to={`${getExternalBaseUrl()}/memories`}
            onClick={() => {
              sendAnalytics({ link_page: 'Home', link_name: 'Get Started' })
            }}
            data-testid="get-started"
          >
            {t('ancestors.portrait.link', 'Get Started')}
          </Button>
        </div>
      </div>
      <Separator size="md" />
      <div css={sectionContainerCss} is-mobile={boolAttr(isMobile)}>
        {isMobile && (
          <div>
            <img src={recordImage} css={imageCss} alt={t('ancestors.record.alt', 'Record')} />
            <Separator size="md" />
          </div>
        )}
        <div>
          <HeaderBlock
            size="sm"
            heading={t(
              'ancestors.historical-records.header',
              "Discover your family's story through historical records."
            )}
          />
          <Body1>
            {t(
              'ancestors.historical-records.description',
              "Using our vast collection of records, you can piece together your ancestors' history and bring their stories to life."
            )}
          </Body1>
          <Button
            to={`${getExternalBaseUrl()}/search`}
            onClick={() => {
              sendAnalytics({ link_page: 'Home', link_name: 'View Records Now' })
            }}
            data-testid="view-records-now"
          >
            {t('ancestors.historical-records.action', 'View Records Now')}
          </Button>
        </div>
        {!isMobile && <img src={recordImage} css={imageCss} alt={t('ancestors.record.alt', 'Record')} />}
      </div>
    </div>
  )
}
